/**
 * VirtualBackgroundDialog: 가상 배경 설정 다이얼로그
 * 원본: wehago-conference RenewalComponents/Headers/Footer/ToolBox/VirtualBackgroundButton/
 *   VirtualBackgroundDialog.js + VirtualBackgroundDialogPreview.js + VirtualBackgroundDialogButtonList.js
 *
 * 구조: gnb_pop_scale > gnb_pop.round (482px) > header + virtualBgContent
 * 버튼 레이아웃: btnItemWrap > btnItem > btnItem__btn
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { uiActions } from '@/store/slices/uiSlice';
import { virtualBackgroundActions, type VirtualBackgroundType } from '@/store/slices/virtualBackgroundSlice';
import { createVirtualBackgroundEffect } from '@/utils/createVirtualBackgroundEffect';
import type { JitsiStreamBackgroundEffect } from '@/utils/JitsiStreamBackgroundEffect';

// 프리셋 배경 이미지 목록 (원본 backgroundImageList 기반)
const BACKGROUND_IMAGES = [
  { id: 1, name: '은은한 배경', src: '/images/vbg/background-1.jpg' },
  { id: 2, name: '카페', src: '/images/vbg/background-2.jpg' },
  { id: 3, name: '오피스', src: '/images/vbg/background-3.jpg' },
  { id: 4, name: '노을', src: '/images/vbg/background-4.jpg' },
  { id: 5, name: '해변', src: '/images/vbg/background-5.jpg' },
  { id: 6, name: '도시', src: '/images/vbg/background-6.jpg' },
  { id: 7, name: '기하학', src: '/images/vbg/background-7.jpg' },
  { id: 8, name: '오로라', src: '/images/vbg/background-8.jpg' },
  { id: 9, name: '모던', src: '/images/vbg/background-9.jpg' },
];

const VirtualBackgroundDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const ui = useAppSelector((s) => s.ui);
  const vbg = useAppSelector((s) => s.virtualBackground);

  const [selectedType, setSelectedType] = useState<VirtualBackgroundType>(vbg.type);
  const [selectedImageId, setSelectedImageId] = useState<number | null>(
    vbg.type === 'image' ? (BACKGROUND_IMAGES.find((b) => b.src === vbg.imageUrl)?.id ?? null) : null
  );
  const [customImages, setCustomImages] = useState<{ id: number; name: string; src: string }[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 미리보기용 refs (원본 VirtualBackgroundDialogPreview 방식)
  const videoRef = useRef<HTMLVideoElement>(null);
  const rawStreamRef = useRef<MediaStream | null>(null);
  const effectRef = useRef<JitsiStreamBackgroundEffect | null>(null);

  if (!ui.isVirtualBackgroundOpen) return null;

  const handleClose = () => dispatch(uiActions.setVirtualBackgroundOpen(false));

  const handleConfirm = () => {
    const img = [...BACKGROUND_IMAGES, ...customImages].find((b) => b.id === selectedImageId);
    dispatch(
      virtualBackgroundActions.setBackground({
        type: selectedType,
        imageUrl: selectedType === 'image' ? (img?.src ?? undefined) : undefined,
        blurValue: selectedType === 'blur' ? 80 : selectedType === 'slight_blur' ? 30 : 0,
      })
    );
    handleClose();
  };

  return (
    <>
      {/* 배경 오버레이 */}
      <div
        className="rtcCustoms_vBg__backdrop"
        onClick={handleClose}
      />

      {/* gnb_pop_scale — 원본: marginLeft '-397px', width '482px' */}
      <div className="gnb_pop_scale rtcCustoms_vBg__popScale">
        <div className="gnb_pop round rtcCustoms_vBg__pop">
          {/* 화살표 */}
          <span className="gnb_poparrow rtcCustoms_vBg__arrow" />

          {/* 타이틀 */}
          <div className="gnb_pop_titbx">
            <span className="pop_tit">화상회의 배경설정</span>
            <p className="subDesc">배경 및 효과를 설정해주세요.</p>
            <button type="button" className="LUX_basic_btn btn_clr" onClick={handleClose}>
              <span className="sp_lux">닫기</span>
            </button>
          </div>

          {/* 본문 */}
          <div className="gnb_popbx_area">
            <div className="gnb_popbx_in">
              <div className="virtualBgContent">

                {/* 미리보기 (원본 VirtualBackgroundDialogPreview) */}
                <VirtualBackgroundPreview
                  videoRef={videoRef}
                  rawStreamRef={rawStreamRef}
                  effectRef={effectRef}
                  selectedType={selectedType}
                  selectedImageId={selectedImageId}
                  customImages={customImages}
                  isOpen={ui.isVirtualBackgroundOpen}
                />

                {/* 버튼 목록 */}
                <div className="virtualBgSet">
                  {/* 섹션1: 효과 및 블러 없음 */}
                  <div className="virtualBgSet__section rtcCustoms_vBg__section">
                    <div className="virtualBgSet__titlePack">
                      <h2 className="virtualBgSet__title nodot">효과 및 블러 없음</h2>
                    </div>
                    <div className="virtualBgSet__btnPack rtcCustoms_vBg__btnPack">
                      <div className="btnItemWrap">
                        {/* 배경없음 */}
                        <div className="btnItem">
                          <button
                            type="button"
                            onClick={() => onSelect('none', undefined, setSelectedType, setSelectedImageId, videoRef, rawStreamRef, effectRef, customImages)}
                            className={`btnItem__btn${selectedType === 'none' ? ' btnItem__btn--selected' : ''}`}
                          >
                            <span className="btnItem__icon btnItem__icon--bgEmpty" />
                            <span className="btnItem__text rtcCustoms_vBg__btnText">배경없음</span>
                          </button>
                        </div>

                        {/* 블러 80% */}
                        <div className="btnItem">
                          <button
                            type="button"
                            onClick={() => onSelect('blur', undefined, setSelectedType, setSelectedImageId, videoRef, rawStreamRef, effectRef, customImages)}
                            className={`btnItem__btn${selectedType === 'blur' ? ' btnItem__btn--selected' : ''}`}
                          >
                            <span className="btnItem__icon btnItem__icon--blur80" />
                            <span className="btnItem__text rtcCustoms_vBg__btnText">블러 80%</span>
                          </button>
                        </div>

                        {/* 블러 30% */}
                        <div className="btnItem">
                          <button
                            type="button"
                            onClick={() => onSelect('slight_blur', undefined, setSelectedType, setSelectedImageId, videoRef, rawStreamRef, effectRef, customImages)}
                            className={`btnItem__btn${selectedType === 'slight_blur' ? ' btnItem__btn--selected' : ''}`}
                          >
                            <span className="btnItem__icon btnItem__icon--blur30" />
                            <span className="btnItem__text rtcCustoms_vBg__btnText">블러 30%</span>
                          </button>
                        </div>

                      </div>
                    </div>
                  </div>

                  {/* 섹션2: 배경 이미지 */}
                  <div className="virtualBgSet__section rtcCustoms_vBg__section">
                    <div className="virtualBgSet__titlePack">
                      <h2 className="virtualBgSet__title nodot">배경</h2>
                    </div>
                    <div className="virtualBgSet__btnPack rtcCustoms_vBg__btnPack">
                      <div className="btnItemWrap">
                        {/* 이미지 추가 */}
                        <div className="btnItem">
                          <button
                            type="button"
                            className="btnItem__btn btnItem__btn--add"
                            onClick={() => fileInputRef.current?.click()}
                            title="이미지 추가"
                          >
                            <span className="rtcCustoms_vBg__addIcon">+</span>
                          </button>
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            className="rtcCustoms_vBg__fileInput"
                            onChange={(e) => handleImageUpload(e, customImages, setCustomImages, setSelectedType, setSelectedImageId, videoRef, rawStreamRef, effectRef)}
                          />
                        </div>

                        {[...BACKGROUND_IMAGES, ...customImages].map((img) => (
                          <div key={img.id} className="btnItem">
                            <button
                              type="button"
                              onClick={() => onSelect('image', img.id, setSelectedType, setSelectedImageId, videoRef, rawStreamRef, effectRef, customImages)}
                              className={`btnItem__btn${selectedType === 'image' && selectedImageId === img.id ? ' btnItem__btn--selected' : ''}`}
                              style={{ backgroundImage: `url(${img.src})` }}
                              title={img.name}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 확인/닫기 버튼 */}
              <div className="btnbx rtcCustoms_vBg__footer">
                <button
                  type="button"
                  className="LUX_basic_btn LUX_renewal Confirm Default basic"
                  onClick={handleClose}
                >
                  <span>닫기</span>
                </button>
                <button
                  type="button"
                  className="LUX_basic_btn LUX_renewal Confirm Default basic2"
                  onClick={handleConfirm}
                >
                  <span>적용</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// ─── 미리보기 서브컴포넌트 ────────────────────────────────────────────────────

interface PreviewProps {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  rawStreamRef: React.MutableRefObject<MediaStream | null>;
  effectRef: React.MutableRefObject<JitsiStreamBackgroundEffect | null>;
  selectedType: VirtualBackgroundType;
  selectedImageId: number | null;
  customImages: { id: number; name: string; src: string }[];
  isOpen: boolean;
}

const VirtualBackgroundPreview: React.FC<PreviewProps> = ({
  videoRef, rawStreamRef, effectRef, selectedType, selectedImageId, customImages, isOpen,
}) => {
  const stopEffect = useCallback(() => {
    effectRef.current?.stopEffect();
    effectRef.current = null;
  }, [effectRef]);

  const applyPreview = useCallback((
    type: VirtualBackgroundType,
    imageId: number | null,
    images: { id: number; name: string; src: string }[],
  ) => {
    const rawStream = rawStreamRef.current;
    if (!rawStream || !videoRef.current) return;
    stopEffect();

    if (type === 'none') {
      videoRef.current.srcObject = rawStream;
      return;
    }

    const bgType = (type === 'blur' || type === 'slight_blur') ? 'blur' : 'image';
    const blurVal = type === 'blur' ? 80 : type === 'slight_blur' ? 30 : undefined;
    const img = type === 'image' && imageId !== null ? images.find((b) => b.id === imageId) : null;
    if (type === 'image' && !img) { videoRef.current.srcObject = rawStream; return; }

    const videoEl = videoRef.current;
    createVirtualBackgroundEffect({
      backgroundType: bgType,
      virtualSource: bgType === 'image' && img ? img.src : undefined,
      blurValue: blurVal,
    }).then((effect) => {
      if (!effect || !rawStreamRef.current || !videoEl) return;
      effectRef.current = effect;
      videoEl.srcObject = effect.startEffect(rawStreamRef.current);
    }).catch(() => {
      if (videoEl && rawStreamRef.current) videoEl.srcObject = rawStreamRef.current;
    });
  }, [videoRef, rawStreamRef, effectRef, stopEffect]);

  // 다이얼로그 열릴 때 카메라 스트림 시작
  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;
    navigator.mediaDevices.getUserMedia({ video: true, audio: false })
      .then((stream) => {
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        rawStreamRef.current = stream;
        applyPreview(selectedType, selectedImageId, [...BACKGROUND_IMAGES, ...customImages]);
      })
      .catch((err) => console.warn('[VBG Preview] camera error', err));

    return () => {
      cancelled = true;
      stopEffect();
      rawStreamRef.current?.getTracks().forEach((t) => t.stop());
      rawStreamRef.current = null;
      if (videoRef.current) videoRef.current.srcObject = null;
    };
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // 배경 선택 변경 시 미리보기 갱신
  useEffect(() => {
    if (!rawStreamRef.current) return;
    applyPreview(selectedType, selectedImageId, [...BACKGROUND_IMAGES, ...customImages]);
  }, [selectedType, selectedImageId, applyPreview, customImages]);

  return (
    <video
      ref={videoRef}
      autoPlay
      muted
      playsInline
      className="rtcCustoms_vBg__preview"
    />
  );
};

// ─── 배경 선택 핸들러 (inline callbacks 대신 모듈 레벨 함수) ─────────────────

function stopEffectFn(effectRef: React.MutableRefObject<JitsiStreamBackgroundEffect | null>) {
  effectRef.current?.stopEffect();
  effectRef.current = null;
}

function applyPreviewFn(
  type: VirtualBackgroundType,
  imageId: number | null,
  images: { id: number; name: string; src: string }[],
  videoRef: React.RefObject<HTMLVideoElement | null>,
  rawStreamRef: React.MutableRefObject<MediaStream | null>,
  effectRef: React.MutableRefObject<JitsiStreamBackgroundEffect | null>,
) {
  const rawStream = rawStreamRef.current;
  if (!rawStream || !videoRef.current) return;
  stopEffectFn(effectRef);

  if (type === 'none') {
    videoRef.current.srcObject = rawStream;
    return;
  }

  const bgType = (type === 'blur' || type === 'slight_blur') ? 'blur' : 'image';
  const blurVal = type === 'blur' ? 80 : type === 'slight_blur' ? 30 : undefined;
  const img = type === 'image' && imageId !== null ? images.find((b) => b.id === imageId) : null;
  if (type === 'image' && !img) { videoRef.current.srcObject = rawStream; return; }

  const videoEl = videoRef.current;
  createVirtualBackgroundEffect({
    backgroundType: bgType,
    virtualSource: bgType === 'image' && img ? img.src : undefined,
    blurValue: blurVal,
  }).then((effect) => {
    if (!effect || !rawStreamRef.current || !videoEl) return;
    effectRef.current = effect;
    videoEl.srcObject = effect.startEffect(rawStreamRef.current);
  }).catch(() => {
    if (videoEl && rawStreamRef.current) videoEl.srcObject = rawStreamRef.current;
  });
}

function onSelect(
  type: VirtualBackgroundType,
  imgId: number | undefined,
  setSelectedType: React.Dispatch<React.SetStateAction<VirtualBackgroundType>>,
  setSelectedImageId: React.Dispatch<React.SetStateAction<number | null>>,
  videoRef: React.RefObject<HTMLVideoElement | null>,
  rawStreamRef: React.MutableRefObject<MediaStream | null>,
  effectRef: React.MutableRefObject<JitsiStreamBackgroundEffect | null>,
  customImages: { id: number; name: string; src: string }[],
) {
  const newImgId = imgId ?? null;
  setSelectedType(type);
  setSelectedImageId(newImgId);
  applyPreviewFn(type, newImgId, [...BACKGROUND_IMAGES, ...customImages], videoRef, rawStreamRef, effectRef);
}

function handleImageUpload(
  e: React.ChangeEvent<HTMLInputElement>,
  customImages: { id: number; name: string; src: string }[],
  setCustomImages: React.Dispatch<React.SetStateAction<{ id: number; name: string; src: string }[]>>,
  setSelectedType: React.Dispatch<React.SetStateAction<VirtualBackgroundType>>,
  setSelectedImageId: React.Dispatch<React.SetStateAction<number | null>>,
  videoRef: React.RefObject<HTMLVideoElement | null>,
  rawStreamRef: React.MutableRefObject<MediaStream | null>,
  effectRef: React.MutableRefObject<JitsiStreamBackgroundEffect | null>,
) {
  const file = e.target.files?.[0];
  if (!file) return;
  const url = URL.createObjectURL(file);
  const newImg = { id: Date.now(), name: file.name, src: url };
  const newCustomImages = [...customImages, newImg];
  setCustomImages(newCustomImages);
  setSelectedType('image');
  setSelectedImageId(newImg.id);
  applyPreviewFn('image', newImg.id, [...BACKGROUND_IMAGES, ...newCustomImages], videoRef, rawStreamRef, effectRef);
  e.target.value = '';
}

export default VirtualBackgroundDialog;
